/**
 * PdfCanvas — renders the pages of a PDF (via react-pdf) and reports each page's
 * rendered pixel size, so callers can position field overlays with percent coords.
 *
 * Responsive: the page width is the container's available width, capped at `maxWidth`,
 * so it fits phones as well as desktop. Children render as an absolutely-positioned
 * overlay on top of each page, receiving { pageNumber, width, height }.
 *
 * Windowed rendering: only pages near the viewport are actually mounted as a real
 * react-pdf <Page> (each one is its own decoded bitmap + canvas). Pages outside that
 * window show a plain placeholder sized to match. Without this, a long or scan-heavy
 * document renders every page's canvas at once on load, which is enough to exhaust a
 * mobile Safari tab's memory and crash it repeatedly (live report, 2026-08-13: a
 * signer's phone showed "A problem repeatedly occurred" on a 13.8MB envelope - the
 * page itself was the crash, resending the email/link changed nothing).
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Loader2 } from 'lucide-react';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

// How far outside the viewport a page is kept mounted. Generous enough that a normal
// scroll never shows a blank placeholder flash, small enough to cap peak memory to a
// handful of pages regardless of total document length.
const RENDER_MARGIN_PX = 1200;

function Spinner({ label = 'Loading document…' }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 p-12 text-sm text-gray-400">
      <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
      <span>{label}</span>
    </div>
  );
}

export default function PdfCanvas({ fileUrl, loading = false, width: maxWidth = 780, renderOverlay, onFirstPageSize }) {
  const [numPages, setNumPages] = useState(0);
  const [sizes, setSizes] = useState({});
  const [activePages, setActivePages] = useState(() => new Set([1]));
  const [pageWidth, setPageWidth] = useState(maxWidth);
  const containerRef = useRef(null);
  const observerRef = useRef(null);
  const nodesRef = useRef(new Map()); // pageNumber -> wrapper DOM node

  // Fit the page to the container (capped at maxWidth) and track resizes.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const update = () => {
      const avail = el.clientWidth - 8; // small gutter
      setPageWidth(Math.max(240, Math.min(maxWidth, avail)));
    };
    update();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro && ro.observe(el);
    window.addEventListener('resize', update);
    return () => { ro && ro.disconnect(); window.removeEventListener('resize', update); };
  }, [maxWidth]);

  // One shared observer for every page wrapper. Pages within RENDER_MARGIN_PX of the
  // viewport get added to activePages (mounted for real); pages that scroll well past
  // that margin get dropped again, so memory stays bounded no matter how long the
  // document is.
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        setActivePages((prev) => {
          let changed = false;
          const next = new Set(prev);
          for (const entry of entries) {
            const pageNumber = Number(entry.target.dataset.pageNumber);
            if (!pageNumber) continue;
            if (entry.isIntersecting) {
              if (!next.has(pageNumber)) { next.add(pageNumber); changed = true; }
            } else if (next.has(pageNumber)) {
              next.delete(pageNumber);
              changed = true;
            }
          }
          return changed ? next : prev;
        });
      },
      { rootMargin: `${RENDER_MARGIN_PX}px 0px` }
    );
    observerRef.current = observer;
    nodesRef.current.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [numPages]);

  const registerPageNode = useCallback((pageNumber, node) => {
    const prev = nodesRef.current.get(pageNumber);
    if (prev && observerRef.current) observerRef.current.unobserve(prev);
    if (node) {
      nodesRef.current.set(pageNumber, node);
      if (observerRef.current) observerRef.current.observe(node);
    } else {
      nodesRef.current.delete(pageNumber);
    }
  }, []);

  const onLoad = useCallback(({ numPages: n }) => setNumPages(n), []);

  const onPageRender = useCallback(
    (pageNumber) => (page) => {
      setSizes((s) => ({ ...s, [pageNumber]: { width: page.width, height: page.height } }));
      if (pageNumber === 1 && onFirstPageSize) onFirstPageSize({ width: page.width, height: page.height });
    },
    [onFirstPageSize]
  );

  // Before a page has ever rendered, estimate its placeholder height from page 1's
  // aspect ratio (nearly always uniform across a document) so the placeholder doesn't
  // collapse to zero height and jump the scroll position once it mounts for real.
  const estimatedAspect = sizes[1] ? sizes[1].height / sizes[1].width : 1.414; // A4-ish fallback

  return (
    <div ref={containerRef} className="w-full flex flex-col items-center">
      {!fileUrl ? (
        loading
          ? <Spinner />
          : <div className="text-gray-400 text-sm p-8 text-center">No document loaded.</div>
      ) : (
        <Document file={fileUrl} onLoadSuccess={onLoad}
          loading={<Spinner />}
          error={<div className="p-8 text-sm text-red-500 text-center">Couldn’t display the document. Please refresh, or use Download.</div>}>
          {Array.from({ length: numPages }, (_, i) => {
            const pageNumber = i + 1;
            const size = sizes[pageNumber];
            const isActive = activePages.has(pageNumber);
            const placeholderHeight = size ? size.height : Math.round(pageWidth * estimatedAspect);
            return (
              <div
                key={pageNumber}
                ref={(node) => registerPageNode(pageNumber, node)}
                data-page-number={pageNumber}
                className="relative mx-auto mb-6 bg-white border border-gray-300 shadow-[0_2px_12px_rgba(15,23,42,0.16)] max-w-full"
                style={{ width: pageWidth, height: isActive ? undefined : placeholderHeight }}
              >
                {isActive ? (
                  <Page
                    pageNumber={pageNumber}
                    width={pageWidth}
                    onRenderSuccess={onPageRender(pageNumber)}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                ) : (
                  <div className="w-full h-full bg-white" />
                )}
                {isActive && size && renderOverlay && (
                  <div className="absolute inset-0" style={{ width: size.width, height: size.height }}>
                    {renderOverlay({ pageNumber, width: size.width, height: size.height })}
                  </div>
                )}
              </div>
            );
          })}
        </Document>
      )}
    </div>
  );
}
